import { SocketContext } from "@/Context/SocketContext";
import { useSetting } from "@/hooks/useSetting";
import { useSocketCallback } from "@/hooks/useSocketCallback";
import { useSocketEvent } from "@/hooks/useSocketEvent";
import { DonoTrainSettings } from "@ailenax-overlay/shared";
import { FC, useCallback, useContext, useEffect, useState } from "react";

export const ChatBot: FC = () => {
    const [chatBotDonationAlert, setChatBotDonationAlert] = useSetting("chat-bot-donation-alert");
    const [chatBotKofiResubMessage, setChatBotKofiResubMessage] = useSetting("kofi-resub-message");
    const [settings, setSettings] = useState<DonoTrainSettings | null>(null);
    const socket = useContext(SocketContext);

    const [state, setState] = useState({
        donationAlert: chatBotDonationAlert ?? "",
        kofiResubMessage: chatBotKofiResubMessage ?? "",
    });

    useSocketCallback("get-donotrain-settings", setSettings);
    useSocketEvent("dono-train-settings-update", (e) => {
        setSettings(e.settings);
    });

    useEffect(() => {
        setState({
            donationAlert: chatBotDonationAlert ?? "",
            kofiResubMessage: chatBotKofiResubMessage ?? "",
        });
    }, [chatBotDonationAlert, chatBotKofiResubMessage, settings]);

    const settingRow = (name: string, title: string, value: string | number, onChange: (e: React.ChangeEvent<HTMLInputElement>) => void) => {
        return (
            <div className="relative z-0 w-full mb-5 group">
                <input type="text" name={name} id={name} className="block py-2.5 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 border-gray-300 appearance-none dark:text-white dark:border-gray-600 dark:focus:border-blue-500 focus:outline-none focus:ring-0 focus:border-blue-600 peer" value={value} onChange={onChange} />
                <label htmlFor={name} className="peer-focus:font-medium absolute text-sm text-gray-500 dark:text-gray-400 duration-300 transform -translate-y-6 scale-75 top-3 -z-10 origin-[0] peer-focus:start-0 rtl:peer-focus:translate-x-1/4 peer-focus:text-blue-600 peer-focus:dark:text-blue-500 peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6">{title}</label>
            </div>
        );
    }

    const saveSettings = useCallback(() => {
        setChatBotDonationAlert(state.donationAlert);
        setChatBotKofiResubMessage(state.kofiResubMessage);

        if (settings !== null) {
            socket.emit("update-donotrain-settings", settings);
        }
    }, [state, setChatBotDonationAlert, setChatBotKofiResubMessage, socket, settings]);

    return (
        <div className="max-h-screen min-w-xl flex flex-col items-center justify-center">
            <h1 className="text-4xl text-center mb-5" aria-label="Chat-Bot">
                Chat-Bot
            </h1>
            <div className="w-1/2 col-span-12 mt-5">
                <form className="flex flex-col max-w-5xl mx-auto dark:bg-gray-800 p-5 rounded-3xl">
                    <div className="flex flex-row gap-6">
                        {settingRow("donation_alert", "Donation alert", state.donationAlert, (e) => setState((prev) => ({ ...prev, donationAlert: e.target.value })))}
                    </div>
                    <div className="flex flex-row gap-6 dark:text-gray-500 text-sm">
                        Supported Variables: {`{user}, {amount}`}
                    </div>
                </form>
            </div>

            <div className="w-1/2 col-span-12 mt-5">
                <form className="flex flex-col max-w-5xl mx-auto dark:bg-gray-800 p-5 rounded-3xl">
                    <div className="flex flex-row gap-6">
                        {settingRow("kofi_resub_message", "Ko-fi Resub Message", state.kofiResubMessage, (e) => setState((prev) => ({ ...prev, kofiResubMessage: e.target.value })))}
                    </div>
                    <div className="flex flex-row gap-6 dark:text-gray-500 text-sm">
                        Supported Variables: {`{user}, {amount}, {tier}`}
                    </div>
                </form>
            </div>

            <div className="w-1/2 col-span-12 mt-5">
                <form className="flex flex-col max-w-5xl mx-auto dark:bg-gray-800 p-5 rounded-3xl">
                    <div className="flex flex-row gap-6">
                        {settingRow("donation_train_ready_message", "Donation Train Ready Message", settings?.trainReadyMessage ?? "", (e) => setSettings(prev => {
                            if (prev !== null) {
                                return { ...prev, trainReadyMessage: e.target.value };
                            }
                            return prev;
                        }))}
                    </div>
                </form>
            </div>

            <div className="w-1/2 col-span-12 mt-5">
                <form className="flex flex-col max-w-5xl mx-auto dark:bg-gray-800 p-5 rounded-3xl">
                    <div className="flex flex-row gap-6">
                        {settingRow("donation_train_primed_message", "Donation Train Primed Message", settings?.trainPrimedMessage ?? "", (e) => setSettings(prev => {
                            if (prev !== null) {
                                return { ...prev, trainPrimedMessage: e.target.value };
                            }
                            return prev;
                        }))}
                    </div>
                </form>
            </div>

            <div className="w-1/2 col-span-12 mt-5">
                <form className="flex flex-col max-w-5xl mx-auto dark:bg-gray-800 p-5 rounded-3xl">
                    <div className="flex flex-row gap-6">
                        {settingRow("donation_train_start_message", "Donation Train Start Message", settings?.trainStartMessage ?? "", (e) => setSettings(prev => {
                            if (prev !== null) {
                                return { ...prev, trainStartMessage: e.target.value };
                            }
                            return prev;
                        }))}
                    </div>
                </form>
            </div>

            <div className="w-1/2 col-span-12 mt-5">
                <form className="flex flex-col max-w-5xl mx-auto dark:bg-gray-800 p-5 rounded-3xl">
                    <div className="flex flex-row gap-6">
                        {settingRow("donation_train_end_message", "Donation Train End Message", settings?.trainEndMessage ?? "", (e) => setSettings(prev => {
                            if (prev !== null) {
                                return { ...prev, trainEndMessage: e.target.value };
                            }
                            return prev;
                        }))}
                    </div>
                </form>
            </div>

            <button className="mt-5 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors" onClick={saveSettings}>
                Save
            </button>
        </div>
    );
}